/* eslint-disable react/jsx-no-useless-fragment */
import { ResidentItem } from '@lmc/ts-common'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { ExtraErrorText, ExtraText, GeneralText } from '../styledComponents'
import { EditTooltip } from './EditTooltip'
import useHomeType from 'hooks/useHomeType'
import { getExtra } from '../utils/utils'
import DownloadForm from './DownloadForm'

type GeneralDNACPRSectionProps = {
  resident: ResidentItem
  testId?: string
}

export const GeneralDNACPRSection = ({
  resident,
  testId,
}: GeneralDNACPRSectionProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const { isDoLSOverride } = useHomeType()

  const hasAllergies = resident?.residentAllergies?.length > 0

  const hasNoKnownAllergies = resident.noKnownAllergies === true

  return (
    <CoreSectionWrapper
      data-testid={
        testId ? `${testId}-dnacpr-section` : 'resident-dnacpr-section'
      }
    >
      <CoreSection
        data-testid={testId ? `${testId}-dnacpr-info` : 'resident-dnacpr-info'}
      >
        <StyledCoreLabel
          level={2}
          data-testid={
            testId ? `${testId}-dnacpr-label` : 'resident-dnacpr-label'
          }
        >
          {t(supConstants.DNACPR)}
        </StyledCoreLabel>
        <GeneralText
          weight="bold"
          data-testid={
            testId ? `${testId}-dnacpr-value` : 'resident-dnacpr-value'
          }
        >
          <EditTooltip title={t(supConstants.DNACPR_TOOLTIP)}>
            <>
              {(resident?.dnacpr === null || resident?.dnacpr === undefined) &&
                t(supConstants.NO_INFORMATION)}
              {resident?.dnacpr === true && t(supConstants.DO_NOT_CPR)}
              {resident?.dnacpr === false && t(supConstants.CPR)}
            </>
          </EditTooltip>
        </GeneralText>
        <DnacprContainer
          data-testid={
            testId ? `${testId}-dnacpr-details` : 'resident-dnacpr-details'
          }
        >
          {resident?.dnacprDetails && (
            <StyledCoreLabel
              level={2}
              data-testid={
                testId
                  ? `${testId}-dnacpr-details-text`
                  : 'resident-dnacpr-details-text'
              }
            >
              {resident?.dnacprDetails}
            </StyledCoreLabel>
          )}
          {resident.dnacprForm && resident.dnacpr && (
            <StyledCoreLabel level={2}>•</StyledCoreLabel>
          )}
          {resident.dnacprForm && (
            <DownloadForm
              fileUrl={resident.dnacprForm}
              fileName={`${resident.name.first} ${resident.name.last}'s DNACPR Form`}
              testId={testId}
            >
              <Text level={2}>{t(supConstants.VIEW_DNACPR_FORM)}</Text>
            </DownloadForm>
          )}
        </DnacprContainer>
      </CoreSection>
      <CoreSection
        data-testid={
          testId ? `${testId}-dols-section` : 'resident-dols-section'
        }
      >
        <StyledCoreLabel
          level={2}
          data-testid={testId ? `${testId}-dols-label` : 'resident-dols-label'}
        >
          {isDoLSOverride
            ? t(supConstants.DEPRIVATION_LIBERTY)
            : t(supConstants.LIBERTY)}
        </StyledCoreLabel>
        <GeneralText
          weight="bold"
          data-testid={testId ? `${testId}-dols-value` : 'resident-dols-value'}
        >
          <EditTooltip
            title={
              isDoLSOverride
                ? t(supConstants.DOLS_TOOLTIP)
                : t(supConstants.LPS_TOOLTIP)
            }
          >
            {resident?.dols ? t(supConstants.YES) : t(supConstants.NO)}
          </EditTooltip>
        </GeneralText>
        {getExtra('dols', resident) === t(supConstants.REVIEW_DATE_OVERDUE) ? (
          <ExtraErrorText
            level={2}
            data-testid={
              testId
                ? `${testId}-dols-overdue-text`
                : 'resident-dols-overdue-text'
            }
          >
            {t(supConstants.REVIEW_DATE_OVERDUE)}
          </ExtraErrorText>
        ) : (
          <ExtraText
            level={2}
            data-testid={
              testId ? `${testId}-dols-extra-text` : 'resident-dols-extra-text'
            }
          >
            {getExtra('dols', resident)}
          </ExtraText>
        )}
      </CoreSection>
      {(hasAllergies || hasNoKnownAllergies) && (
        <CoreSection
          data-testid={
            testId
              ? `${testId}-allergies-section`
              : 'resident-allergies-section'
          }
        >
          <StyledCoreLabel
            level={2}
            data-testid={
              testId ? `${testId}-allergies-label` : 'resident-allergies-label'
            }
          >
            {t(supConstants.ALLERGIES)}
          </StyledCoreLabel>
          <GeneralText
            weight="bold"
            data-testid={
              testId ? `${testId}-allergies-value` : 'resident-allergies-value'
            }
          >
            <EditTooltip title={t(supConstants.ALLERGY_TOOLTIP)}>
              <>
                {hasNoKnownAllergies && t(supConstants.NO_KNOWN_ALLERGIES)}
                {!hasNoKnownAllergies
                  ? hasAllergies
                    ? resident?.residentAllergies.map((allergy) => (
                        <AllergyTitle
                          key={allergy.causativeAgent}
                          data-testid={
                            testId
                              ? `${testId}-allergy-${allergy.causativeAgent}`
                              : `resident-allergy-${allergy.causativeAgent}`
                          }
                        >
                          {allergy.causativeAgent}
                        </AllergyTitle>
                      ))
                    : t(supConstants.NO)
                  : null}
              </>
            </EditTooltip>
          </GeneralText>
        </CoreSection>
      )}
    </CoreSectionWrapper>
  )
}

const StyledCoreLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const CoreSectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`

const CoreSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 15px;
  }
`

const DnacprContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const AllergyTitle = styled.div`
  display: flex;
  flex-direction: column;
`
